import Phaser from 'phaser';
import { BEEHIVE_THROWER } from '../../config';
import { Enemy } from '../Enemy';
import { Projectile } from '../Projectile';
import { StickFigureGraphics } from '../../rendering/StickFigureGraphics';
import { BuildingSlot, ChainUpgradeEffect, TargetPriority } from '../../types';
import { selectByPriority } from '../units/BaseUnit';

export class BeehiveThrower {
    scene: Phaser.Scene;
    x: number;
    y: number;
    level: number;
    position: BuildingSlot;
    unitType: string = 'beehive_thrower';
    graphics: Phaser.GameObjects.Graphics;
    sprite: Phaser.GameObjects.Sprite;
    private attackTimer: number = 0;
    private projectiles: Projectile[];
    stackCount: number = 1;

    // Chain upgrade bonuses
    swarmDpsMult: number = 1;
    swarmRadiusMult: number = 1;
    bonusSwarmDuration: number = 0;
    swarmSlowEffect: number = 0;
    globalDamageMult: number = 1;
    attackSpeedMult: number = 1;

    constructor(
        scene: Phaser.Scene,
        x: number,
        y: number,
        position: BuildingSlot,
        projectiles: Projectile[]
    ) {
        this.scene = scene;
        this.x = x;
        this.y = y;
        this.level = 1;
        this.position = position;
        this.graphics = scene.add.graphics();
        this.sprite = scene.add.sprite(x, y, 'building_beehive');
        this.sprite.setOrigin(0.5, 110 / 128);
        this.projectiles = projectiles;
        this.stackCount = 1;
        this.draw();
    }

    getDamage(): number {
        return BEEHIVE_THROWER.swarmDps * this.stackCount * this.swarmDpsMult * this.globalDamageMult;
    }

    getSwarmDuration(): number {
        return BEEHIVE_THROWER.swarmDuration + this.bonusSwarmDuration;
    }

    getSwarmSlowEffect(): number {
        return this.swarmSlowEffect > 0 ? this.swarmSlowEffect : BEEHIVE_THROWER.slowEffect;
    }

    addStack(): void {
        this.stackCount++;
    }

    applyChainUpgrade(effect: ChainUpgradeEffect): void {
        if (effect.swarmDpsMult !== undefined) this.swarmDpsMult *= effect.swarmDpsMult;
        if (effect.swarmRadiusMult !== undefined) this.swarmRadiusMult *= effect.swarmRadiusMult;
        if (effect.swarmDurationBonus !== undefined) this.bonusSwarmDuration += effect.swarmDurationBonus;
        if (effect.swarmSlowEffect !== undefined) this.swarmSlowEffect = effect.swarmSlowEffect;
        this.level++;
        this.draw();
    }

    update(time: number, delta: number, enemies: Enemy[]): void {
        this.attackTimer -= delta;
        if (this.attackTimer <= 0) {
            const target = this.findTarget(enemies);
            if (target) {
                this.attackTimer = BEEHIVE_THROWER.attackInterval / this.attackSpeedMult;
                this.fire(target);
            }
        }
        this.draw();
    }

    private findTarget(enemies: Enemy[]): Enemy | null {
        const range = BEEHIVE_THROWER.range;
        const candidates: Enemy[] = [];
        for (const enemy of enemies) {
            if (enemy.isDead) continue;
            if (enemy.enemyType === 'boulder') continue;
            if (enemy.isBurrowed) continue;
            if (enemy.isGoblin) continue;
            const dx = enemy.x - this.x;
            const dy = enemy.y - this.y;
            if (Math.sqrt(dx * dx + dy * dy) < range) {
                candidates.push(enemy);
            }
        }
        return selectByPriority(candidates, 'mostClustered' as TargetPriority, this.x, this.y);
    }

    private fire(target: Enemy): void {
        const proj = new Projectile(this.scene, this.x, this.y - 12, {
            damage: this.getDamage(),
            speed: BEEHIVE_THROWER.projectileSpeed,
            piercing: false,
            projectileType: 'beehive',
            targetX: target.x,
            targetY: target.y,
            lobbing: true,
            slowEffect: this.getSwarmSlowEffect(),
            slowDuration: this.getSwarmDuration(),
        });
        this.projectiles.push(proj);
    }

    draw(): void {
        this.sprite.setTexture('building_beehive');
        this.graphics.clear();
        if (this.level > 1) {
            this.graphics.fillStyle(0xffd700, 1);
            for (let i = 0; i < Math.min(this.level - 1, 5); i++) {
                this.graphics.fillCircle(this.x - 6 + i * 3, this.y + 8, 1.5);
            }
        }
    }

    destroy(): void {
        this.graphics.destroy();
        this.sprite.destroy();
    }
}
